import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, FilterIcon, GlobeIcon, BriefcaseIcon, GraduationCapIcon, MapPinIcon, CalendarIcon, ArrowRightIcon, BookOpenIcon, SparklesIcon, AwardIcon, CheckIcon, TrendingUpIcon } from 'lucide-react';
// Sample scholarship data
const scholarships = [{
  id: 'sch001',
  title: 'Elite Track: Computer Science at MIT',
  track: 'Elite',
  university: 'Massachusetts Institute of Technology',
  program: 'Computer Science',
  location: 'United States',
  deadline: '2023-12-15',
  coverageTypes: ['Tuition', 'Living Allowance', 'Travel', 'Books & Materials'],
  description: 'Full scholarship for outstanding Emirati students to pursue Computer Science at MIT, with a focus on AI and machine learning.',
  eligibility: ['UAE Nationals', 'GPA 3.8+', 'IELTS 7.5+'],
  logo: 'MIT'
}, {
  id: 'sch003',
  title: 'ADNOC Future Energy Leaders',
  track: 'Partnership',
  university: 'Stanford University',
  program: 'Petroleum Engineering',
  location: 'United States',
  deadline: '2023-11-15',
  coverageTypes: ['Tuition', 'Living Allowance', 'Travel', 'Books & Materials'],
  description: 'ADNOC-sponsored scholarship for future energy leaders with guaranteed employment upon graduation.',
  eligibility: ['UAE Nationals', 'GPA 3.5+', 'IELTS 6.5+'],
  employer: 'ADNOC',
  logo: 'ADNOC'
}, {
  id: 'sch004',
  title: 'Mubadala Tech Innovators',
  track: 'Partnership',
  university: 'Multiple Options',
  program: 'Computer Science / Data Science',
  location: 'Multiple Locations',
  deadline: '2023-12-01',
  coverageTypes: ['Tuition', 'Living Allowance'],
  description: 'Mubadala-sponsored scholarship for tech innovators with flexible university options from the approved list.',
  eligibility: ['UAE Nationals', 'GPA 3.5+', 'IELTS 6.5+'],
  employer: 'Mubadala',
  logo: 'Mubadala'
}, {
  id: 'sch006',
  title: 'Emirates Future Leaders',
  track: 'Partnership',
  university: 'Multiple Options',
  program: 'Aviation Management',
  location: 'Multiple Locations',
  deadline: '2023-11-20',
  coverageTypes: ['Tuition', 'Living Allowance', 'Travel'],
  description: 'Emirates-sponsored scholarship for future aviation leaders with guaranteed employment upon graduation.',
  eligibility: ['UAE Nationals', 'GPA 3.3+', 'IELTS 6.5+'],
  employer: 'Emirates',
  logo: 'Emirates'
}];
const Scholarships: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('All Locations');
  const [programFilter, setProgramFilter] = useState('All Programs');
  // Filter scholarships based on filters
  const filteredScholarships = scholarships.filter(scholarship => {
    // Filter by search query
    if (searchQuery && !scholarship.title.toLowerCase().includes(searchQuery.toLowerCase()) && !scholarship.university.toLowerCase().includes(searchQuery.toLowerCase()) && !scholarship.program.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    // Filter by location
    if (locationFilter !== 'All Locations' && scholarship.location !== locationFilter) {
      return false;
    }
    // Filter by program
    if (programFilter !== 'All Programs' && scholarship.program !== programFilter) {
      return false;
    }
    return true;
  });
  // Get unique locations and programs for filters
  const locations = ['All Locations', ...new Set(scholarships.map(s => s.location))];
  const programs = ['All Programs', ...new Set(scholarships.map(s => s.program))];
  // Format date for display
  const formatDate = dateString => {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  // Calculate days remaining until deadline
  const getDaysRemaining = dateString => {
    const deadline = new Date(dateString);
    const today = new Date();
    const diffTime = deadline.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  return <Layout persona="Student" data-id="element-5340">
      <div className="max-w-7xl mx-auto" data-id="element-5341">
        {/* Hero section with introduction */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-xl mb-8 overflow-hidden shadow-lg" data-id="element-5342">
          <div className="p-8 relative" data-id="element-5343">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -translate-y-1/3 translate-x-1/3" data-id="element-5344"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-white opacity-5 rounded-full translate-y-1/3 -translate-x-1/3" data-id="element-5345"></div>
            <h2 className="text-3xl font-bold mb-3 text-white" data-id="element-5346">
              Discover International Scholarship Opportunities
            </h2>
            <p className="text-lg mb-6 text-white text-opacity-90 max-w-3xl" data-id="element-5347">
              Explore prestigious scholarships from the Elite Track at top
              global universities and Partnership Track with leading employers.
            </p>
            <div className="flex flex-wrap gap-4 mt-6" data-id="element-5348">
              <div className="flex items-center bg-white bg-opacity-20 backdrop-blur-sm px-4 py-2.5 rounded-lg shadow-sm transition-all hover:bg-opacity-25" data-id="element-5349">
                <GlobeIcon className="w-5 h-5 mr-2 text-white" data-id="element-5350" />
                <span className="text-white font-medium" data-id="element-5351">
                  Top Global Universities
                </span>
              </div>
              <div className="flex items-center bg-white bg-opacity-20 backdrop-blur-sm px-4 py-2.5 rounded-lg shadow-sm transition-all hover:bg-opacity-25" data-id="element-5352">
                <BriefcaseIcon className="w-5 h-5 mr-2 text-white" data-id="element-5353" />
                <span className="text-white font-medium" data-id="element-5354">
                  Employer Partnerships
                </span>
              </div>
              <div className="flex items-center bg-white bg-opacity-20 backdrop-blur-sm px-4 py-2.5 rounded-lg shadow-sm transition-all hover:bg-opacity-25" data-id="element-5355">
                <GraduationCapIcon className="w-5 h-5 mr-2 text-white" data-id="element-5356" />
                <span className="text-white font-medium" data-id="element-5357">
                  Full Financial Coverage
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and search */}
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm mb-8" data-id="element-5358">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4" data-id="element-5359">
            <div className="relative flex-grow" data-id="element-5360">
              <input type="text" placeholder="Search scholarships by name, university, or program..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm" data-id="element-5361" />
              <SearchIcon className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" data-id="element-5362" />
            </div>
          </div>
          <div className="flex flex-wrap gap-3" data-id="element-5363">
            {/* Location Filter */}
            <div className="relative" data-id="element-5364">
              <select className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 appearance-none pr-10 shadow-sm" value={locationFilter} onChange={e => setLocationFilter(e.target.value)} data-id="element-5365">
                {locations.map(location => <option key={location} value={location} data-id="element-5366">
                    {location}
                  </option>)}
              </select>
              <MapPinIcon className="absolute right-3 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-5367" />
            </div>
            {/* Program Filter */}
            <div className="relative" data-id="element-5368">
              <select className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 appearance-none pr-10 shadow-sm" value={programFilter} onChange={e => setProgramFilter(e.target.value)} data-id="element-5369">
                {programs.map(program => <option key={program} value={program} data-id="element-5370">
                    {program}
                  </option>)}
              </select>
              <BookOpenIcon className="absolute right-3 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-5371" />
            </div>
          </div>
        </div>

        {/* Scholarship Cards */}
        {filteredScholarships.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-5372">
            {filteredScholarships.map(scholarship => {
          const isElite = scholarship.track === 'Elite';
          const daysRemaining = getDaysRemaining(scholarship.deadline);
          return <div key={scholarship.id} className={`group relative rounded-xl overflow-hidden transition-all duration-300 transform hover:-translate-y-1 ${isElite ? 'bg-gradient-to-br from-primary-50 to-white border border-primary-200 shadow-md' : 'bg-white border border-gray-200 shadow-sm hover:shadow-md hover:border-secondary-300'}`} onClick={() => navigate(`/student/scholarships/${scholarship.id}`)} data-id="element-5373">
                  {/* Card content */}
                  <div className="p-6 pt-12" data-id="element-5374">
                    {/* Elite/Partnership badge - positioned absolutely */}
                    <div className="absolute top-3 right-3 z-10" data-id="element-5375">
                      {isElite ? <div className="flex items-center bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-sm" data-id="element-5376">
                          <SparklesIcon className="w-3 h-3 mr-1" data-id="element-5377" />
                          Elite Track
                        </div> : <div className="flex items-center bg-secondary-600 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-sm" data-id="element-5378">
                          <BriefcaseIcon className="w-3 h-3 mr-1" data-id="element-5379" />
                          Partnership
                        </div>}
                    </div>
                    <div className="flex items-center mb-4" data-id="element-5380">
                      <div className={`h-12 w-12 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 ${isElite ? 'bg-primary-600' : 'bg-secondary-600'}`} data-id="element-5381">
                        {scholarship.logo}
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-5382">
                        <h3 className={`font-semibold ${isElite ? 'text-primary-900' : 'text-gray-900'} text-lg mb-1 line-clamp-1 pr-2`} data-id="element-5383">
                          {scholarship.title}
                        </h3>
                        <div className="flex items-center" data-id="element-5384">
                          <GraduationCapIcon className="w-3.5 h-3.5 text-gray-500 mr-1.5 flex-shrink-0" data-id="element-5385" />
                          <span className="text-sm text-gray-600 truncate" data-id="element-5386">
                            {scholarship.university}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 mb-4" data-id="element-5387">
                      <div className="flex items-start" data-id="element-5388">
                        <BookOpenIcon className="w-4 h-4 text-gray-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-5389" />
                        <span className="text-sm text-gray-700" data-id="element-5390">
                          {scholarship.program}
                        </span>
                      </div>
                      <div className="flex items-start" data-id="element-5391">
                        <MapPinIcon className="w-4 h-4 text-gray-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-5392" />
                        <span className="text-sm text-gray-700" data-id="element-5393">
                          {scholarship.location}
                        </span>
                      </div>
                      {scholarship.employer && <div className="flex items-start" data-id="element-5394">
                          <BriefcaseIcon className="w-4 h-4 text-gray-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-5395" />
                          <span className="text-sm text-gray-700" data-id="element-5396">
                            {scholarship.employer}
                          </span>
                        </div>}
                    </div>
                    {/* Coverage badges */}
                    <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-5397">
                      {scholarship.coverageTypes.slice(0, 3).map((type, index) => <span key={index} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isElite ? 'bg-primary-100 text-primary-800' : 'bg-green-100 text-green-800'}`} data-id="element-5398">
                            <CheckIcon className="w-3 h-3 mr-1" data-id="element-5399" />
                            {type}
                          </span>)}
                      {scholarship.coverageTypes.length > 3 && <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5400">
                          +{scholarship.coverageTypes.length - 3} more
                        </span>}
                    </div>
                    {/* Description */}
                    <div className="text-sm text-gray-600 mb-4 line-clamp-2" data-id="element-5401">
                      {scholarship.description}
                    </div>
                    {/* Deadline and application button */}
                    <div className="border-t border-gray-100 pt-4 mt-4" data-id="element-5402">
                      <div className="flex items-center justify-between mb-4" data-id="element-5403">
                        <div className="flex items-center" data-id="element-5404">
                          <CalendarIcon className="w-4 h-4 text-gray-500 mr-1.5" data-id="element-5405" />
                          <span className="text-sm text-gray-700" data-id="element-5406">
                            Deadline: {formatDate(scholarship.deadline)}
                          </span>
                        </div>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${daysRemaining < 14 ? 'bg-red-100 text-red-800' : daysRemaining < 30 ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-5407">
                          {daysRemaining} days left
                        </div>
                      </div>
                      <div className="flex space-x-2" data-id="element-5408">
                        <button className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center ${isElite ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-secondary-600 text-white hover:bg-secondary-700'}`} onClick={e => {
                    e.stopPropagation();
                    navigate(`/student/scholarships/${scholarship.id}`);
                  }} data-id="element-5409">
                          View Details
                          <ArrowRightIcon className="w-4 h-4 ml-1.5" data-id="element-5410" />
                        </button>
                        <button className={`py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${isElite ? 'border border-primary-600 text-primary-600 hover:bg-primary-50' : 'border border-secondary-600 text-secondary-600 hover:bg-secondary-50'}`} onClick={e => {
                    e.stopPropagation();
                    navigate(`/student/scholarship-application/${scholarship.id}`);
                  }} data-id="element-5411">
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                </div>;
        })}
          </div> : <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm text-center" data-id="element-5412">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-5413">
              <SearchIcon className="w-8 h-8 text-gray-400" data-id="element-5414" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-5415">
              No scholarships found
            </h3>
            <p className="text-gray-500 mb-4" data-id="element-5416">
              Try adjusting your filters or search criteria
            </p>
            <button onClick={() => {
          setSearchQuery('');
          setLocationFilter('All Locations');
          setProgramFilter('All Programs');
        }} className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors" data-id="element-5417">
              Reset Filters
            </button>
          </div>}

        {/* My Applications Section */}
        <div className="bg-gradient-to-r from-secondary-600 to-secondary-700 p-6 rounded-xl shadow-md mb-8" data-id="element-5418">
          <div className="flex flex-col md:flex-row items-center justify-between" data-id="element-5419">
            <div className="text-white mb-4 md:mb-0" data-id="element-5420">
              <h3 className="text-xl font-semibold mb-2" data-id="element-5421">
                Track Your Scholarship Applications
              </h3>
              <p className="text-white text-opacity-90" data-id="element-5422">
                View status updates and manage your submitted scholarship
                applications
              </p>
            </div>
            <button onClick={() => navigate('/student/my-scholarships')} className="px-6 py-3 bg-white text-secondary-700 rounded-lg font-medium hover:bg-gray-100 transition-colors flex items-center shadow-sm" data-id="element-5423">
              My Applications
              <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-5424" />
            </button>
          </div>
        </div>

        {/* Elite Track Highlight */}
        <div className="bg-white rounded-xl border border-primary-200 shadow-md overflow-hidden mb-8" data-id="element-5425">
          <div className="p-6 border-b border-primary-100 bg-primary-50" data-id="element-5426">
            <div className="flex items-center" data-id="element-5427">
              <AwardIcon className="w-6 h-6 text-primary-600 mr-3" data-id="element-5428" />
              <h2 className="text-xl font-semibold text-primary-900" data-id="element-5429">
                Elite Track Scholarships
              </h2>
            </div>
          </div>
          <div className="p-6" data-id="element-5430">
            <div className="flex flex-col md:flex-row gap-6" data-id="element-5431">
              <div className="flex-1" data-id="element-5432">
                <h3 className="text-lg font-medium text-gray-900 mb-3" data-id="element-5433">
                  Why Choose Elite Track?
                </h3>
                <ul className="space-y-2" data-id="element-5434">
                  <li className="flex items-start" data-id="element-5435">
                    <TrendingUpIcon className="w-5 h-5 text-primary-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-5436" />
                    <span className="text-gray-700" data-id="element-5437">
                      Access to the world's top-ranked universities
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-5438">
                    <TrendingUpIcon className="w-5 h-5 text-primary-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-5439" />
                    <span className="text-gray-700" data-id="element-5440">
                      Comprehensive financial coverage including tuition, living
                      expenses, and travel
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-5441">
                    <TrendingUpIcon className="w-5 h-5 text-primary-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-5442" />
                    <span className="text-gray-700" data-id="element-5443">
                      Priority academic support and mentorship
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-5444">
                    <TrendingUpIcon className="w-5 h-5 text-primary-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-5445" />
                    <span className="text-gray-700" data-id="element-5446">
                      Exclusive networking opportunities with global leaders
                    </span>
                  </li>
                </ul>
              </div>
              <div className="flex-1" data-id="element-5447">
                <h3 className="text-lg font-medium text-gray-900 mb-3" data-id="element-5448">
                  Eligibility Requirements
                </h3>
                <ul className="space-y-2" data-id="element-5449">
                  <li className="flex items-start" data-id="element-5450">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-5451" />
                    <span className="text-gray-700" data-id="element-5452">UAE National</span>
                  </li>
                  <li className="flex items-start" data-id="element-5453">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-5454" />
                    <span className="text-gray-700" data-id="element-5455">Minimum GPA of 3.5+</span>
                  </li>
                  <li className="flex items-start" data-id="element-5456">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-5457" />
                    <span className="text-gray-700" data-id="element-5458">
                      Strong English proficiency (IELTS 7.0+ or equivalent)
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-5459">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-5460" />
                    <span className="text-gray-700" data-id="element-5461">
                      Demonstrated leadership potential and academic excellence
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-6 text-center" data-id="element-5462">
              <button onClick={() => navigate('/student/scholarships/sch001')} className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors inline-flex items-center" data-id="element-5463">
                <SparklesIcon className="w-5 h-5 mr-2" data-id="element-5464" />
                Learn More About Elite Track
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>;
};
export default Scholarships;